import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../../../hooks/redux';
import { ButtonClassNames, ModalVariants, InputRegexPattern } from '../../../../../models/Common';
import { FormInputText } from '../../../../inputs/FormInputText';
import { CustomMUIButton } from '../../../buttons/CustomMUIButton';
import { ApiError } from '../../../../../models/ApiError';
import { modalSlice } from '../../../../../store/reducers/ModalSlice';
import { iptvApi } from '../../../../../services/apiServices/user/iptv';
import { useCustomSnackbar } from '../../../../../hooks/useCustomEnqueueSnackbar';
import { FormInputPassword } from '../../../../inputs/FormInputPassword';
import { getErrorMessage } from '../../../../../utils';

interface EditBrizTvEmailProps {
  accountId: string | number;
  handleClose: () => void;
}

interface IEditBrizTvEmail {
  email: string;
  currentPassword: string;
}

export const EditBrizTvEmail: FC<EditBrizTvEmailProps> = ({ accountId, handleClose }) => {
  const { t } = useTranslation();
  const methods = useForm<IEditBrizTvEmail>();
  const { handleSubmit, setError } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [editBrizTv, { isLoading }] = iptvApi.useEditBrizTvMutation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;

  const handleEditEmail = async ({ email, currentPassword }: IEditBrizTvEmail) => {
    try {
      await editBrizTv({ email, accountId, currentPassword })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.emailBrizTvSuccessfullyChanged'), 'success');
          handleClose();
        });
    } catch (err) {
      const error = err as ApiError;
      if (error?.data?.error?.errors) {
        const apiErrors = error.data.error.errors as Partial<Record<keyof IEditBrizTvEmail, string>>;
        const apiErrorsKeys = Object.keys(apiErrors) as Array<keyof IEditBrizTvEmail>;
        apiErrorsKeys.forEach((element) => {
          setError(element, {
            type: 'server',
            message: apiErrors[element],
          });
        });
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const handleForgotPassword = () => {
    dispatch(openModal({ modalVariants: ModalVariants.LogOut }));
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('modals.changeEmail')}</article>
      </div>
      <form className="modals__form" onSubmit={handleSubmit(handleEditEmail)}>
        <FormProvider {...methods}>
          <FormInputPassword
            name="currentPassword"
            className="primary"
            label={t('auth.enterPassLk')}
            autoComplete="new-password"
            isRequired
            minLength={6}
            maxLength={16}
            fieldHasName
          />
          <FormInputText
            name="email"
            className="primary"
            label={t('input.email')}
            autoComplete="new-password"
            isRequired
            minLength={4}
            maxLength={99}
            pattern={InputRegexPattern.Email}
          />
          <div className="modals__btns-wrapper">
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('buttons.save')}
                type="submit"
                className={ButtonClassNames.PrimaryFulfilled}
                isBold
                isLoading={isLoading}
              />
            </div>
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('auth.forgotMyPassword')}
                handleClick={handleForgotPassword}
                className={ButtonClassNames.SecondaryOutlined}
                isBold
                type="button"
              />
            </div>
          </div>
        </FormProvider>
      </form>
    </div>
  );
};
